import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Project } from "../data/projects";
import { useApp } from "../context/AppContext";
import GidiIcon from "../components/GidiIcon";
import TextType from "../components/TextType";
import WorkInProgressScroll from "../components/WorkInProgressScroll";
import ImageWithLoader from "../components/ImageWithLoader";
import "./ProjectPage.css";

interface ProjectPageProps {
  projects: Project[];
}

const ProjectPage: React.FC<ProjectPageProps> = ({ projects }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { mitMode } = useApp();
  const navigate = useNavigate();
  const [markdown, setMarkdown] = useState<string>("");
   // eslint-disable-next-line 
  const [loading, setLoading] = useState(true);
   // eslint-disable-next-line 
  const [error, setError] = useState<string | null>(null);
  const [isCompact, setIsCompact] = useState(false);
  const [heroImage, setHeroImage] = useState<string>("");

  const currentProject = projects.find((p) => p.id === projectId);
  const currentIndex = projects.findIndex((p) => p.id === projectId);
  const previousProject = currentIndex > 0 ? projects[currentIndex - 1] : null;
  const nextProject =
    currentIndex < projects.length - 1 ? projects[currentIndex + 1] : null;

  // Handle scroll to make navigation compact
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      setIsCompact(scrollTop > 100); // Make compact after scrolling 100px
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

    // Navigation handlers
  const handlePreviousProject = () => {
    if (previousProject) {
      // Force scroll to top immediately
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      navigate(`/projects/${previousProject.id}`);
    }
  };

  const handleNextProject = () => {
    if (nextProject) {
      // Force scroll to top immediately
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      navigate(`/projects/${nextProject.id}`);
    }
  };



  // Scroll to top whenever projectId changes
  useEffect(() => {
    // Force scroll to top with multiple methods
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Also try after a small delay to ensure content is loaded
    setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 100);
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !currentProject) return;

    const loadProjectData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load markdown
        const response = await fetch(`/projects/${projectId}/${projectId}.md`);
        if (!response.ok) {
          throw new Error("Failed to load project details");
        }
        const text = await response.text();
        setMarkdown(text);
        
        // Use cover image for hero section
        setHeroImage(currentProject.coverImage);
      } catch (err) {
        setError("Failed to load project details");
        console.error("Error loading markdown:", err);
      } finally {
        setLoading(false);
      }
    };

    loadProjectData();
  }, [projectId, currentProject]);

  // Only show error if we have no current project and no projectId
  if (!projectId && !currentProject) {
    return (
      <div className="project-not-found">
        <h1 className="project-not-found-title">Project Not Found</h1>
        <Link to="/projects" className="project-not-found-link">
          Back to Work
        </Link>
      </div>
    );
  }

  // If we have a current project, show it even while loading
  if (currentProject) {
    return (
      <div className="project-page">
        {/* Navigation bar */}
        <nav className={`nav ${isCompact ? "compact" : ""}`}>
          <div className="nav-home">
            <Link to="/" className="nav-link-special">
              <GidiIcon size={27} />
            </Link>
          </div>
          <div className="nav-other">
            <Link to="/projects" className="nav-link">
              projects
            </Link>
            <Link to="/about" className="nav-link">
              about
            </Link>
          </div>
        </nav>

        {/* Hero Section with Cover Image */}
        <div className="project-hero" key={projectId}>
          <div className="project-hero-info">
            {/* Work In Progress Badge */}
            {currentProject.workInProgress && (
              <WorkInProgressScroll />
            )}

            <h1 className="project-hero-title">{currentProject.title}</h1>


            {currentProject.description && (
              <p className="project-hero-caption">
                {currentProject.description}
              </p>
            )}

            {currentProject.awardImage && (
              <div className="project-award-image">
                <ImageWithLoader
                  src={currentProject.awardImage}
                  alt="Award"
                  className="award-logo"
                  loaderSize={20}
                />
              </div>
            )}
                        {currentProject.tags && currentProject.tags.length > 0 && (
              <div className="tag-list" aria-label="Project tags" >
                {currentProject.tags.map((tag) => (
                  <span className="tag-pill" key={tag}>{tag}</span>
                ))}
              </div>
            )}
          </div>
          
          
          <ImageWithLoader
            src={heroImage || currentProject.coverImage}
            alt={currentProject.title}
            className="project-hero-image"
            loaderSize={60}
          />
          
          {/* Meta Information Bubble */}
          {currentProject?.meta && (
            <div className="meta-bubble">
              <p>{currentProject.meta}</p>
            </div>
          )}
        </div>

        {/* Content area */}
        <div className="project-content">
          <div className="project-content-container">
                        <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                h1: ({ children }) => <h1 className="md-h1">{children}</h1>,
                h2: ({ children }) => <h2 className="md-h2">{children}</h2>,
                h3: ({ children }) => <h3 className="md-h3">{children}</h3>,
                h4: ({ children }) => (
                  <>
                    {mitMode && currentProject?.mitTag && (
                      <div className="mit-tag-bubble-inline">
                        <p className="mit-tag-text">{currentProject.mitTag}</p>
                        <p className="mit-tag-subheading">Relation to Critical Matter & Tangible Media</p>
                      </div>
                    )}
                    <TextType 
                      text={String(children)}
                      className="md-h4"
                      duration={70}
                      delay={0}
                    />
                  </>
                ),
                p: ({ children, ...props }) => <p className="md-p" {...props}>{children}</p>,
                ul: ({ children }) => <ul className="md-ul">{children}</ul>,
                ol: ({ children }) => <ol className="md-ol">{children}</ol>,
                li: ({ children }) => <li className="md-li">{children}</li>,
                blockquote: ({ children }) => (
                  <blockquote className="md-blockquote">{children}</blockquote>
                ),
                code: ({ className, children, ...props }) => {
                  const match = /language-(\w+)/.exec(className || "");
                  return match ? (
                    <pre className="md-pre">
                      <code className={`md-code ${className}`} {...props}>
                        {children}
                      </code>
                    </pre>
                  ) : (
                    <code className="md-inline-code" {...props}>
                      {children}
                    </code>
                  );
                },
                strong: ({ children }) => (
                  <strong className="md-strong">{children}</strong>
                ),
                em: ({ children }) => <em className="md-em">{children}</em>,
                img: ({ src, alt, className, ...props }: any) => {
                  // Check if alt text ends with [border] marker
                  const hasBorderMarker = alt?.endsWith('[border]');
                  const cleanAlt = hasBorderMarker ? alt.slice(0, -8).trim() : alt;
                  
                  // Combine classes: use provided class if exists, otherwise use border marker
                  const imageClass = className 
                    ? `md-img ${className}` 
                    : hasBorderMarker 
                      ? 'md-img md-img-bordered' 
                      : 'md-img';
                  
                  return (
                    <ImageWithLoader 
                      src={src || ''} 
                      alt={cleanAlt || ''} 
                      className={imageClass}
                      loaderSize={40}
                    />
                  );
                },
                a: ({ href, children }) => (
                  <a
                    href={href}
                    className="md-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {children}
                  </a>
                )
              }}
            >
              {markdown}
            </ReactMarkdown>
          </div>
        </div>

        {/* Bottom Navigation - Previous/Next Projects */}
        <div className="project-bottom-nav">
          <div className="project-nav-section">
            <svg
              className="project-bottom-nav-wave"
              viewBox="0 0 900 10"
              fill="white"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
              style={{ display: "block", width: "100%", height: "10px" }}
            >
              <path
                d="M0 5 Q 22.5 0, 45 5 T 90 5 T 135 5 T 180 5 T 225 5 T 270 5 T 315 5 T 360 5 T 405 5 T 450 5 T 495 5 T 540 5 T 585 5 T 630 5 T 675 5 T 720 5 T 765 5 T 810 5 T 855 5 T 900 5"
                stroke="#000"
                stroke-width="1"
                fill="none"
              />
            </svg>
            <div className="project-nav-buttons">
              {previousProject && (
                <button
                  onClick={handlePreviousProject}
                  className="project-nav-link project-nav-prev"
                >
                  <span className="nav-arrow">←</span>
                  <span className="nav-text">{previousProject.title}</span>
                </button>
              )}
              {nextProject && (
                <button
                  onClick={handleNextProject}
                  className="project-nav-link project-nav-next"
                >
                  <span className="nav-text">{nextProject.title}</span>
                  <span className="nav-arrow">→</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default return - should never reach here, but TypeScript requires it
  return (
    <div className="project-not-found">
      <h1 className="project-not-found-title">Project Not Found</h1>
      <Link to="/projects" className="project-not-found-link">
        Back to Work
      </Link>
    </div>
  );
};

export default ProjectPage;
